import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.store';
import { usePermission } from './usePermission';
import { useT } from '../i18n';
import { fetchScopedConfig, putScopedConfig } from '../api/config-scope.api';

/**
 * Hook genérico para tabs de configuración a nivel TENANT (Fase 3.2).
 *
 * Reemplaza el patrón `mutationFn: Promise.resolve()` de los tabs stub —
 * cada tab declara solo sus `defaults` (subset de config-catalog.ts) y este
 * hook se encarga de fetch inicial, mutación real contra
 * `PUT /api/:tenantId/config/TENANT/:tenantId`, invalidación y errores del
 * backend (validación del catálogo, permisos por setting).
 */
export function useTenantConfig(defaults: Record<string, unknown>) {
  const t = useT();
  // UUID de la sesión (no el código de la URL/tenant store) y solo con permiso config:read.
  const tenantId = useAuthStore(s => s.user?.tenantId);
  const canReadConfig = usePermission('config:read');
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, unknown>>(defaults);
  const [saved, setSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const queryKey = ['tenant-config', tenantId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => fetchScopedConfig('TENANT', tenantId as string),
    enabled: !!tenantId && canReadConfig,
  });

  useEffect(() => {
    if (!data) return;
    setValues(prev => {
      const next = { ...prev };
      for (const key of Object.keys(defaults)) {
        const resolved = data.effective[key];
        if (resolved) next[key] = resolved.value;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const mutation = useMutation({
    mutationFn: (settings: Record<string, unknown>) =>
      putScopedConfig('TENANT', tenantId as string, settings),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      setErrorMessage(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? t.common.configSaveError;
      setErrorMessage(message);
    },
  });

  function updateField(key: string, value: unknown) {
    setValues(v => ({ ...v, [key]: value }));
  }

  function save(keys?: string[]) {
    const subset = keys
      ? Object.fromEntries(keys.map(k => [k, values[k]]))
      : Object.fromEntries(Object.keys(defaults).map(k => [k, values[k]]));
    mutation.mutate(subset);
  }

  return {
    values,
    updateField,
    save,
    isLoading,
    isSaving: mutation.isPending,
    saved,
    errorMessage,
  };
}
